import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  FileSpreadsheet,
  Layers,
  RefreshCw,
  Sparkles,
  Upload,
  X,
} from "lucide-react";
import {
  api,
  type CannibalizationGroupView,
  type CannibalizationResponse,
  type ClusterView,
  type ImportCanonicalField,
  type ImportCommitResponse,
  type ImportMapping,
  type ImportPreviewResponse,
  type Language,
  type Priority,
  type ImportTriageResponse,
  type TriagedRow,
  type TriageVerdict,
  type TriageDecision,
  type ImportCommitInput,
} from "../../lib/api.js";
import { fileToCsv, SPREADSHEET_ACCEPT } from "../../lib/spreadsheet.js";
import { NewClusterDialog } from "./NewClusterDialog.js";

/** Sentinel for the "+ Create new cluster…" option in the default-cluster picker. */
const NEW_CLUSTER = "__new__";

/**
 * 5-step CSV import wizard:
 *
 *   1. UPLOAD   — drag-drop or pick a file. Frontend reads it as text + posts content to /preview.
 *   2. MAP      — operator confirms or overrides Claude's column→canonical mapping.
 *   3. TRIAGE   — the SEO-expert pass: splits valid from junk (competitor names,
 *                 institutions, spelling variants, garbage), scores impact, assigns
 *                 clusters, and reports where the upload proves OUR taxonomy/scope
 *                 wrong. Operator can override any verdict.
 *   4. GROUP    — Claude proposes cannibalization groupings (primary + secondaries) —
 *                 operator can accept, regroup, or reject before commit.
 *   5. COMMIT   — bulk insert with primary_keyword_id relationships set on secondaries.
 *
 * State machine is intentionally simple (single `step` cursor + per-step state).
 * Each step's "Next" button calls the API for the next step's data so the UI
 * can show real progress + Claude warnings as they happen.
 */

const CANONICAL_LABELS: Record<ImportCanonicalField, { label: string; description: string }> = {
  keyword: { label: "Keyword text", description: "The primary search term (required column for any row to import)" },
  language: { label: "Language", description: "'he', 'en', 'hebrew', 'english'" },
  volume: { label: "Search volume", description: "Monthly searches, integer" },
  difficulty: { label: "Difficulty", description: "KD / Competition score 0–100" },
  cpc: { label: "CPC", description: "Cost per click, e.g. 'CPC (USD)'" },
  intent: { label: "Search intent", description: "informational / commercial / transactional / navigational" },
  cluster: { label: "Cluster / topic", description: "Cluster label — matches to a cluster id at commit time" },
  primary_keyword: { label: "Primary keyword (parent)", description: "When the CSV already classifies primary/secondary" },
  related_keywords: { label: "Related keywords", description: "Comma- or pipe-separated variations" },
  url: { label: "Top URL", description: "Competitor URL — stored for later mining, not used for routing" },
  ignore: { label: "(Skip this column)", description: "Preserve the value in imported_meta but don't drive workflow" },
};

type Step = "upload" | "map" | "triage" | "group" | "commit" | "done";

export function ImportWizard({ onClose }: { onClose: () => void }) {
  const [step, setStep] = useState<Step>("upload");
  const [csvContent, setCsvContent] = useState<string>("");
  const [filename, setFilename] = useState<string>("");
  const [preview, setPreview] = useState<ImportPreviewResponse | null>(null);
  const [mappings, setMappings] = useState<ImportMapping[]>([]);
  const [triage, setTriage] = useState<ImportTriageResponse | null>(null);
  const [triageOverrides, setTriageOverrides] = useState<Map<string, TriageVerdict>>(new Map());
  const [cannibalization, setCannibalization] = useState<CannibalizationResponse | null>(null);
  const [acceptedGroups, setAcceptedGroups] = useState<Set<string>>(new Set()); // primary text keys
  const [defaultClusterId, setDefaultClusterId] = useState<string | null>(null);
  const [defaultLanguage, setDefaultLanguage] = useState<Language>("he");
  const [defaultPriority, setDefaultPriority] = useState<Priority>("medium");
  const [commitResult, setCommitResult] = useState<ImportCommitResponse | null>(null);

  const clustersQuery = useQuery({
    queryKey: ["pipeline", "clusters"],
    queryFn: () => api.listClusters(),
  });

  return (
    <>
      <div className="fixed inset-0 z-[60] bg-ink/30 backdrop-blur-sm animate-fade-in" onClick={onClose} />
      <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 pointer-events-none">
        <div className="pointer-events-auto flex max-h-[90vh] w-full max-w-4xl flex-col rounded-2xl bg-white shadow-rail animate-fade-in">
          <Header step={step} onClose={onClose} />

          <div className="flex-1 overflow-y-auto p-6">
            {step === "upload" && (
              <UploadStep
                onPreviewReady={(content, fname, p) => {
                  setCsvContent(content);
                  setFilename(fname);
                  setPreview(p);
                  setMappings(p.mappings);
                  setStep("map");
                }}
              />
            )}

            {step === "map" && preview && (
              <MapStep
                preview={preview}
                mappings={mappings}
                onChangeMapping={(idx, target) =>
                  setMappings((prev) => prev.map((m, i) => (i === idx ? { ...m, target } : m)))
                }
                onBack={() => setStep("upload")}
                onNext={() => setStep("triage")}
              />
            )}

            {step === "triage" && csvContent && (
              <TriageStep
                csvContent={csvContent}
                mappings={mappings}
                triage={triage}
                overrides={triageOverrides}
                onTriageReady={setTriage}
                onOverride={(kw, v) =>
                  setTriageOverrides((prev) => {
                    const next = new Map(prev);
                    next.set(kw, v);
                    return next;
                  })
                }
                onBack={() => setStep("map")}
                onNext={() => setStep("group")}
              />
            )}

            {step === "group" && csvContent && (
              <GroupStep
                csvContent={csvContent}
                mappings={mappings}
                cannibalization={cannibalization}
                acceptedGroups={acceptedGroups}
                onAnalyse={async () => {
                  // triggered by GroupStep child via mutation
                }}
                onCannibalizationReady={(c) => {
                  setCannibalization(c);
                  // Default — accept everything strong (confidence ≥ 0.6)
                  setAcceptedGroups(new Set(c.groups.filter((g) => g.confidence >= 0.6).map((g) => g.primary)));
                }}
                onToggleGroup={(primary) => {
                  setAcceptedGroups((prev) => {
                    const next = new Set(prev);
                    if (next.has(primary)) next.delete(primary);
                    else next.add(primary);
                    return next;
                  });
                }}
                onBack={() => setStep("triage")}
                onNext={() => setStep("commit")}
              />
            )}

            {step === "commit" && cannibalization && (
              <CommitStep
                fileName={filename}
                preview={preview}
                cannibalization={cannibalization}
                acceptedGroups={acceptedGroups}
                clusters={clustersQuery.data?.clusters ?? []}
                defaultClusterId={defaultClusterId}
                defaultLanguage={defaultLanguage}
                defaultPriority={defaultPriority}
                onChangeDefaultCluster={setDefaultClusterId}
                onChangeDefaultLanguage={setDefaultLanguage}
                onChangeDefaultPriority={setDefaultPriority}
                csvContent={csvContent}
                mappings={mappings}
                triageDecisions={
                  triage
                    ? triage.rows.map((r) => ({
                        keyword: r.keyword,
                        verdict: triageOverrides.get(r.keyword) ?? r.verdict,
                        reason: r.reason,
                        clusterId: r.clusterId,
                        impact: r.impact,
                      }))
                    : []
                }
                triageEntities={triage?.entities ?? []}
                onBack={() => setStep("group")}
                onCommit={(r) => {
                  setCommitResult(r);
                  setStep("done");
                }}
              />
            )}

            {step === "done" && commitResult && <DoneStep result={commitResult} onClose={onClose} />}
          </div>
        </div>
      </div>
    </>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Header w/ step progress
// ───────────────────────────────────────────────────────────────────────────

function Header({ step, onClose }: { step: Step; onClose: () => void }) {
  const steps: { id: Step; label: string }[] = [
    { id: "upload", label: "Upload" },
    { id: "map", label: "Map columns" },
    { id: "triage", label: "Triage" },
    { id: "group", label: "Group" },
    { id: "commit", label: "Confirm" },
  ];
  const stepIdx = steps.findIndex((s) => s.id === step);

  return (
    <header className="flex items-center justify-between gap-4 border-b border-line px-6 py-4">
      <div className="flex items-center gap-3 min-w-0">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-50 text-brand-700">
          <FileSpreadsheet className="h-4 w-4" strokeWidth={2.2} />
        </div>
        <div className="min-w-0">
          <div className="text-sm font-semibold text-ink">Import keywords from CSV</div>
          <div className="text-2xs text-muted">SEMrush / Ahrefs / custom — Claude detects your column mapping automatically</div>
        </div>
      </div>
      <div className="hidden items-center gap-3 lg:flex">
        {steps.map((s, i) => (
          <div key={s.id} className="flex items-center gap-2">
            <div
              className={
                i < stepIdx
                  ? "flex h-5 w-5 items-center justify-center rounded-full bg-accent text-white"
                  : i === stepIdx
                  ? "flex h-5 w-5 items-center justify-center rounded-full bg-brand-700 text-white"
                  : "flex h-5 w-5 items-center justify-center rounded-full bg-line text-muted"
              }
            >
              {i < stepIdx ? <CheckCircle2 className="h-3 w-3" strokeWidth={2.4} /> : <span className="text-2xs font-semibold">{i + 1}</span>}
            </div>
            <span
              className={
                i === stepIdx
                  ? "text-xs font-medium text-ink"
                  : i < stepIdx
                  ? "text-xs text-muted"
                  : "text-xs text-muted/60"
              }
            >
              {s.label}
            </span>
          </div>
        ))}
      </div>
      <button onClick={onClose} className="btn-icon shrink-0">
        <X className="h-4 w-4" />
      </button>
    </header>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// STEP 1 — Upload
// ───────────────────────────────────────────────────────────────────────────

function UploadStep({
  onPreviewReady,
}: {
  onPreviewReady: (content: string, filename: string, preview: ImportPreviewResponse) => void;
}) {
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const preview = useMutation({
    mutationFn: (csvContent: string) => api.importPreview(csvContent),
  });

  async function handleFile(file: File) {
    setError(null);
    if (file.size > 18 * 1024 * 1024) {
      setError(`File too large (${(file.size / 1024 / 1024).toFixed(1)} MB) — 18 MB max. Split into smaller batches.`);
      return;
    }
    try {
      const text = await fileToCsv(file);
      const result = await preview.mutateAsync(text);
      onPreviewReady(text, file.name, result);
    } catch (e) {
      setError((e as Error).message);
    }
  }

  return (
    <div className="space-y-4">
      <label
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          const file = e.dataTransfer.files[0];
          if (file) handleFile(file);
        }}
        className={
          dragOver
            ? "flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-brand bg-brand-50 p-12 text-center transition"
            : "flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-line bg-canvas p-12 text-center transition hover:border-brand-200 hover:bg-brand-50/40"
        }
      >
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-50 text-brand-700">
          <Upload className="h-6 w-6" strokeWidth={1.8} />
        </div>
        <div className="mt-3 text-sm font-medium text-ink">Drag your CSV or Excel file here, or click to choose</div>
        <div className="mt-1 text-xs text-muted">
          CSV or .xlsx · up to 18 MB · headers in row 1 · any column layout (SEMrush, Ahrefs, GKP, custom — Claude figures it out)
        </div>
        <input
          type="file"
          accept={`${SPREADSHEET_ACCEPT},text/plain`}
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleFile(file);
          }}
        />
      </label>

      {preview.isPending && (
        <div className="rounded-lg bg-info-soft px-4 py-3 text-xs text-blue-700">
          <RefreshCw className="mr-2 inline h-3.5 w-3.5 animate-spin" />
          Parsing CSV + asking Claude to detect column mapping (~3–5s)…
        </div>
      )}

      {error && (
        <div className="rounded-lg bg-danger-soft px-4 py-3 text-xs text-red-700 flex items-start gap-2">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="rounded-lg border border-line bg-canvas-deep/40 p-4 text-xs text-muted">
        <div className="font-semibold text-ink">What happens after upload</div>
        <ul className="mt-2 list-disc pl-5 space-y-1">
          <li>Step 2 — Confirm Claude's column mapping (override any cell)</li>
          <li>
            Step 3 — <strong>Triage</strong>: valid vs junk (competitor names, institutions, spelling
            variants, garbage), impact score + cluster per keyword, plus a report on what the upload
            reveals about gaps in our own taxonomy/scope
          </li>
          <li>Step 4 — Claude groups overlapping keywords into primary + secondary (cannibalization detection)</li>
          <li>Step 5 — You approve + set defaults, then bulk-insert</li>
        </ul>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// STEP 2 — Map columns
// ───────────────────────────────────────────────────────────────────────────

function MapStep({
  preview,
  mappings,
  onChangeMapping,
  onBack,
  onNext,
}: {
  preview: ImportPreviewResponse;
  mappings: ImportMapping[];
  onChangeMapping: (idx: number, target: ImportCanonicalField) => void;
  onBack: () => void;
  onNext: () => void;
}) {
  const hasKeywordColumn = mappings.some((m) => m.target === "keyword");
  return (
    <div className="space-y-4">
      <div>
        <div className="text-sm font-semibold text-ink">Confirm column mapping</div>
        <p className="mt-1 text-xs text-muted">
          {preview.preview.totalRows.toLocaleString()} rows · {preview.preview.headers.length} columns ·
          mapped by <span className="font-mono">{preview.mapperModel}</span>
        </p>
      </div>

      <div className="overflow-x-auto rounded-lg border border-line">
        <table className="w-full text-xs">
          <thead className="bg-canvas">
            <tr>
              <th className="px-3 py-2 text-left font-semibold uppercase tracking-wider text-2xs text-muted">CSV header</th>
              <th className="px-3 py-2 text-left font-semibold uppercase tracking-wider text-2xs text-muted">Map to</th>
              <th className="px-3 py-2 text-left font-semibold uppercase tracking-wider text-2xs text-muted">Sample values</th>
              <th className="px-3 py-2 text-left font-semibold uppercase tracking-wider text-2xs text-muted">Confidence</th>
            </tr>
          </thead>
          <tbody>
            {mappings.map((m, idx) => {
              const samples = preview.preview.rows.slice(0, 3).map((r) => r[m.source]).filter(Boolean);
              return (
                <tr key={m.source} className="border-t border-line align-top">
                  <td className="px-3 py-2 font-mono text-xs text-ink">{m.source}</td>
                  <td className="px-3 py-2">
                    <select
                      value={m.target}
                      onChange={(e) => onChangeMapping(idx, e.target.value as ImportCanonicalField)}
                      className={
                        m.target === "keyword"
                          ? "input py-1 text-xs ring-1 ring-brand/30"
                          : m.target === "ignore"
                          ? "input py-1 text-xs text-muted"
                          : "input py-1 text-xs"
                      }
                    >
                      {(Object.keys(CANONICAL_LABELS) as ImportCanonicalField[]).map((f) => (
                        <option key={f} value={f}>{CANONICAL_LABELS[f].label}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 py-2 text-2xs text-muted" dir="auto">
                    {samples.length > 0 ? samples.join(", ").slice(0, 80) : "—"}
                  </td>
                  <td className="px-3 py-2 text-2xs text-muted">
                    {(m.confidence * 100).toFixed(0)}%
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {!hasKeywordColumn && (
        <div className="rounded-lg bg-warn-soft px-3 py-2 text-xs text-amber-800 flex items-start gap-2">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>No column mapped to <code className="font-mono">keyword</code> yet. Pick the column containing the search terms.</span>
        </div>
      )}

      <Footer onBack={onBack} onNext={onNext} nextDisabled={!hasKeywordColumn} nextLabel="Continue to grouping" />
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// STEP 3 — Cannibalization grouping
// ───────────────────────────────────────────────────────────────────────────


// ── Triage step ────────────────────────────────────────────────────────────
// The SEO-expert pass. Splits valid from junk before anything reaches the
// pipeline AND reports where the upload proves the system's own taxonomy or
// scope definition wrong — the part a silent importer would hide.

const VERDICT_ORDER: TriageVerdict[] = ["keep", "review", "reject"];

const VERDICT_STYLE: Record<TriageVerdict, string> = {
  keep: "bg-accent-soft text-emerald-800 border-accent/30",
  review: "bg-warn-soft text-amber-800 border-amber-300/40",
  reject: "bg-danger-soft text-red-700 border-danger/30",
};

function TriageStep({
  csvContent,
  mappings,
  triage,
  overrides,
  onTriageReady,
  onOverride,
  onBack,
  onNext,
}: {
  csvContent: string;
  mappings: ImportMapping[];
  triage: ImportTriageResponse | null;
  overrides: Map<string, TriageVerdict>;
  onTriageReady: (t: ImportTriageResponse) => void;
  onOverride: (keyword: string, verdict: TriageVerdict) => void;
  onBack: () => void;
  onNext: () => void;
}) {
  const [openBucket, setOpenBucket] = useState<TriageVerdict | null>("review");
  const [showAll, setShowAll] = useState(false);

  const run = useMutation({
    mutationFn: () => api.importTriage(csvContent, mappings),
    onSuccess: (r) => onTriageReady(r),
  });

  if (!triage && !run.isPending && !run.isError) run.mutate();

  if (run.isPending || !triage) {
    return (
      <div className="rounded-lg bg-info-soft px-4 py-6 text-center text-xs text-blue-700">
        <RefreshCw className="mx-auto h-5 w-5 animate-spin" />
        <div className="mt-2 font-medium">Triaging your keywords…</div>
        <div className="mt-1 text-2xs text-blue-700/70">
          Deduping + folding Hebrew spelling variants, then judging each keyword
          (verdict, entities, audience, cluster, impact). ~35 keywords per Claude call.
        </div>
      </div>
    );
  }

  if (run.isError) {
    return (
      <div className="rounded-lg bg-danger-soft px-4 py-3 text-xs text-red-700 flex items-start gap-2">
        <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>Triage failed: {(run.error as Error).message}</span>
      </div>
    );
  }

  const verdictOf = (r: TriagedRow): TriageVerdict => overrides.get(r.keyword) ?? r.verdict;
  const bucket = (v: TriageVerdict) => triage.rows.filter((r) => verdictOf(r) === v);
  const counts = { keep: bucket("keep").length, review: bucket("review").length, reject: bucket("reject").length };
  const gaps = triage.gaps;

  return (
    <div className="space-y-4">
      {/* Verdict summary */}
      <div className="grid grid-cols-3 gap-2">
        {VERDICT_ORDER.map((v) => (
          <button
            key={v}
            onClick={() => setOpenBucket(openBucket === v ? null : v)}
            className={`rounded-lg border px-3 py-2 text-left transition ${VERDICT_STYLE[v]} ${openBucket === v ? "ring-2 ring-brand-700/30" : ""}`}
          >
            <div className="text-lg font-semibold">{counts[v]}</div>
            <div className="text-2xs uppercase tracking-wide">{v}</div>
          </button>
        ))}
      </div>
      <p className="text-2xs text-muted">
        {triage.total} rows analysed · {triage.llmCalls} Claude call(s) · {triage.entities.length} entities harvested.
        Rejected keywords are still imported, as <code className="font-mono">rejected</code> with a reason — visible and recoverable, never silently dropped.
      </p>

      {/* Gap report — the system auditing its own definitions */}
      {gaps.headline.length > 0 && (
        <div className="rounded-lg border border-brand-700/20 bg-brand-50/50 px-4 py-3">
          <div className="flex items-center gap-2 text-xs font-semibold text-brand-800">
            <AlertCircle className="h-3.5 w-3.5" /> What this upload says about OUR setup
          </div>
          <ul className="mt-2 space-y-1 text-2xs text-ink/80">
            {gaps.headline.slice(0, 8).map((h, i) => (
              <li key={i}>• {h}</li>
            ))}
          </ul>
          {gaps.coverage.unclustered > 0 && (
            <div className="mt-2 text-2xs text-muted">
              {gaps.coverage.unclustered} of {gaps.coverage.keeps} kept keywords ({gaps.coverage.unclusteredPct}%) have no cluster —
              they import fine but stay unplannable until those clusters exist.
            </div>
          )}
        </div>
      )}

      {/* Bucket detail */}
      {openBucket && (
        <div className="rounded-lg border border-line">
          <div className="flex items-center justify-between border-b border-line px-3 py-2">
            <div className="text-xs font-medium text-ink capitalize">{openBucket} ({counts[openBucket]})</div>
            <div className="text-2xs text-muted">Click a verdict chip to override</div>
          </div>
          <div className="max-h-72 overflow-y-auto">
            {bucket(openBucket)
              .slice(0, showAll ? undefined : 40)
              .map((r) => (
                <div key={r.keyword} className="flex items-center gap-2 border-b border-line/60 px-3 py-1.5 last:border-0">
                  <div className="w-10 shrink-0 text-right text-2xs font-semibold text-brand-700">{r.impact}</div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-xs text-ink" dir="auto">{r.keyword}</div>
                    <div className="truncate text-3xs text-muted">
                      {r.volume !== null ? `${r.volume}/mo · ` : ""}
                      {r.reason}
                      {r.clusterId ? ` · ${r.clusterId}` : " · no cluster"}
                      {r.variantOf ? ` · variant of "${r.variantOf}"` : ""}
                    </div>
                  </div>
                  <div className="flex shrink-0 gap-1">
                    {VERDICT_ORDER.map((v) => (
                      <button
                        key={v}
                        onClick={() => onOverride(r.keyword, v)}
                        className={`rounded px-1.5 py-0.5 text-3xs font-medium capitalize transition ${
                          verdictOf(r) === v ? VERDICT_STYLE[v] + " border" : "text-muted hover:bg-line"
                        }`}
                      >
                        {v}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
          </div>
          {bucket(openBucket).length > 40 && (
            <button onClick={() => setShowAll(!showAll)} className="w-full border-t border-line px-3 py-1.5 text-2xs text-brand-700 hover:bg-line/40">
              {showAll ? "Show fewer" : `Show all ${bucket(openBucket).length}`}
            </button>
          )}
        </div>
      )}

      <div className="flex items-center justify-between pt-2">
        <button onClick={onBack} className="btn-ghost text-sm">Back</button>
        <button onClick={onNext} className="btn-primary text-sm">
          Continue with {counts.keep + counts.review} keyword(s)
        </button>
      </div>
    </div>
  );
}

function GroupStep({
  csvContent,
  mappings,
  cannibalization,
  acceptedGroups,
  onCannibalizationReady,
  onToggleGroup,
  onBack,
  onNext,
}: {
  csvContent: string;
  mappings: ImportMapping[];
  cannibalization: CannibalizationResponse | null;
  acceptedGroups: Set<string>;
  onAnalyse: () => Promise<void>;
  onCannibalizationReady: (c: CannibalizationResponse) => void;
  onToggleGroup: (primary: string) => void;
  onBack: () => void;
  onNext: () => void;
}) {
  const analyse = useMutation({
    mutationFn: () => api.importCannibalization(csvContent, mappings),
    onSuccess: (r) => onCannibalizationReady(r),
  });

  // Kick off analysis on first mount if it hasn't been done.
  if (!cannibalization && !analyse.isPending && !analyse.isError) {
    analyse.mutate();
  }

  if (analyse.isPending || !cannibalization) {
    return (
      <div className="rounded-lg bg-info-soft px-4 py-6 text-center text-xs text-blue-700">
        <RefreshCw className="mx-auto h-5 w-5 animate-spin" />
        <div className="mt-2 font-medium">Asking Claude to group overlapping keywords…</div>
        <div className="mt-1 text-2xs text-blue-700/70">
          ~5-30s depending on list size. Cost: ~$0.001 per 40 keywords.
        </div>
      </div>
    );
  }

  if (analyse.isError) {
    return (
      <div className="rounded-lg bg-danger-soft px-4 py-3 text-xs text-red-700 flex items-start gap-2">
        <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>Cannibalization analysis failed: {(analyse.error as Error).message}</span>
      </div>
    );
  }

  const acceptedCount = cannibalization.groups.filter((g) => acceptedGroups.has(g.primary)).length;
  const totalSecondariesFolded = cannibalization.groups
    .filter((g) => acceptedGroups.has(g.primary))
    .reduce((sum, g) => sum + g.secondaries.length, 0);
  const articlesAvoided = totalSecondariesFolded; // each folded secondary is one article we don't produce

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
        <KpiSmall label="Total keywords" value={cannibalization.totalKeywords} />
        <KpiSmall label="Suggested groups" value={cannibalization.groups.length} />
        <KpiSmall label="Accepted" value={acceptedCount} tone="brand" />
        <KpiSmall
          label="Articles avoided"
          value={articlesAvoided}
          tone="accent"
          hint="if accepted"
        />
      </div>

      <div className="text-sm font-semibold text-ink">Suggested groupings</div>

      {cannibalization.groups.length === 0 ? (
        <div className="rounded-lg bg-canvas-deep/40 p-4 text-xs text-muted text-center">
          No cannibalization risk detected — every keyword stands on its own.
        </div>
      ) : (
        <ul className="space-y-3">
          {cannibalization.groups.map((g) => (
            <GroupCard
              key={g.primary}
              group={g}
              accepted={acceptedGroups.has(g.primary)}
              onToggle={() => onToggleGroup(g.primary)}
            />
          ))}
        </ul>
      )}

      {cannibalization.standalone.length > 0 && (
        <details className="rounded-lg border border-line bg-canvas-deep/40 p-3">
          <summary className="cursor-pointer text-xs font-semibold text-muted">
            {cannibalization.standalone.length} standalone keyword{cannibalization.standalone.length === 1 ? "" : "s"} (each gets its own article)
          </summary>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {cannibalization.standalone.map((k) => (
              <span key={k} className="rounded-full bg-white px-2 py-0.5 text-2xs ring-1 ring-line" dir="auto">{k}</span>
            ))}
          </div>
        </details>
      )}

      <Footer onBack={onBack} onNext={onNext} nextLabel="Continue to commit" />
    </div>
  );
}

function GroupCard({ group, accepted, onToggle }: { group: CannibalizationGroupView; accepted: boolean; onToggle: () => void }) {
  const confColor =
    group.confidence >= 0.8 ? "bg-accent text-emerald-700" :
    group.confidence >= 0.6 ? "bg-info-soft text-blue-700" :
    "bg-warn-soft text-amber-700";
  return (
    <li
      className={
        accepted
          ? "rounded-lg border-2 border-brand bg-brand-50/40 p-3"
          : "rounded-lg border border-line bg-white p-3 hover:border-brand-200"
      }
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="rounded-full bg-brand-700 px-2 py-0.5 text-2xs font-semibold uppercase tracking-wider text-white">Primary</span>
            <span className="text-sm font-semibold text-ink" dir="auto">{group.primary}</span>
            <span className={`rounded-full px-1.5 py-0.5 text-2xs font-medium ${confColor}`}>
              {(group.confidence * 100).toFixed(0)}% confident
            </span>
          </div>
          <p className="mt-1 text-2xs text-muted">{group.rationale}</p>
          <div className="mt-2 flex flex-wrap gap-1">
            <span className="text-2xs font-semibold uppercase tracking-wider text-muted self-center mr-1">Folds in:</span>
            {group.secondaries.map((s) => (
              <span key={s} className="rounded-full bg-canvas px-2 py-0.5 text-2xs ring-1 ring-line" dir="auto">{s}</span>
            ))}
          </div>
        </div>
        <label className="flex shrink-0 cursor-pointer items-center gap-1.5 text-xs">
          <input type="checkbox" checked={accepted} onChange={onToggle} />
          <span>{accepted ? "Will fold" : "Keep separate"}</span>
        </label>
      </div>
    </li>
  );
}

function KpiSmall({ label, value, tone = "neutral", hint }: { label: string; value: number; tone?: "neutral" | "brand" | "accent"; hint?: string }) {
  const color =
    tone === "brand" ? "text-brand-700" :
    tone === "accent" ? "text-emerald-700" :
    "text-ink";
  return (
    <div className="rounded-lg border border-line bg-white px-3 py-2">
      <div className="text-2xs font-semibold uppercase tracking-wider text-muted">{label}</div>
      <div className={`mt-1 text-xl font-semibold ${color}`}>{value.toLocaleString()}</div>
      {hint && <div className="text-2xs text-muted/70">{hint}</div>}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// STEP 4 — Commit
// ───────────────────────────────────────────────────────────────────────────

function CommitStep({
  fileName,
  preview,
  cannibalization,
  acceptedGroups,
  clusters,
  defaultClusterId,
  defaultLanguage,
  defaultPriority,
  onChangeDefaultCluster,
  onChangeDefaultLanguage,
  onChangeDefaultPriority,
  csvContent,
  mappings,
  triageDecisions,
  triageEntities,
  onBack,
  onCommit,
}: {
  fileName: string;
  preview: ImportPreviewResponse | null;
  cannibalization: CannibalizationResponse;
  acceptedGroups: Set<string>;
  clusters: ClusterView[];
  defaultClusterId: string | null;
  defaultLanguage: Language;
  defaultPriority: Priority;
  onChangeDefaultCluster: (id: string | null) => void;
  onChangeDefaultLanguage: (lang: Language) => void;
  onChangeDefaultPriority: (p: Priority) => void;
  csvContent: string;
  mappings: ImportMapping[];
  triageDecisions: TriageDecision[];
  triageEntities: { name: string; type: string; role: string }[];
  onBack: () => void;
  onCommit: (r: ImportCommitResponse) => void;
}) {
  const acceptedGroupsList = cannibalization.groups.filter((g) => acceptedGroups.has(g.primary));
  const secondariesFolded = acceptedGroupsList.reduce((s, g) => s + g.secondaries.length, 0);
  // Total unique keywords to insert: full count minus the secondaries that get folded.
  const totalToInsert = cannibalization.totalKeywords - secondariesFolded;
  // No, wait: secondaries DO get inserted (as their own rows with primaryKeywordId set). What
  // they DON'T do is generate their own articles. So total inserted = totalKeywords.
  const expectedInsert = cannibalization.totalKeywords;

  const qc = useQueryClient();
  const [newClusterOpen, setNewClusterOpen] = useState(false);
  const [autoClassify, setAutoClassify] = useState(false);

  const commit = useMutation({
    mutationFn: () => api.importCommit({
      csvContent,
      mappings,
      groups: acceptedGroupsList.map((g) => ({ primary: g.primary, secondaries: g.secondaries })),
      defaultClusterId,
      defaultLanguage,
      defaultPriority,
      autoClassify,
      triage: triageDecisions,
      entities: triageEntities as ImportCommitInput["entities"],
    }),
    onSuccess: onCommit,
  });

  void preview; // currently unused — file name + counts are in `fileName` + cannibalization
  void totalToInsert; // exposed via expectedInsert
  return (
    <div className="space-y-4">
      <div>
        <div className="text-sm font-semibold text-ink">Confirm import</div>
        <p className="mt-1 text-xs text-muted">
          File <span className="font-mono">{fileName}</span> · {expectedInsert.toLocaleString()} keywords will be inserted.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3 rounded-lg border border-line p-4 sm:grid-cols-3">
        <div>
          <label className="label">Default cluster (for rows without one)</label>
          <select
            value={defaultClusterId ?? ""}
            onChange={(e) => {
              if (e.target.value === NEW_CLUSTER) { setNewClusterOpen(true); return; }
              onChangeDefaultCluster(e.target.value || null);
            }}
            disabled={autoClassify}
            className="input"
          >
            <option value="">— Unassigned —</option>
            {clusters.map((c) => (
              <option key={c.id} value={c.id}>{c.nameEn}</option>
            ))}
            <option value={NEW_CLUSTER}>+ Create new cluster…</option>
          </select>
          <p className="mt-1 text-2xs text-muted">
            {autoClassify ? "Ignored while auto-classify is on." : "Use “Create new cluster…” to import this whole list as a new cluster."}
          </p>
        </div>
        <div>
          <label className="label">Default language</label>
          <select
            value={defaultLanguage}
            onChange={(e) => onChangeDefaultLanguage(e.target.value as Language)}
            className="input"
          >
            <option value="he">Hebrew</option>
            <option value="en">English</option>
          </select>
        </div>
        <div>
          <label className="label">Default priority</label>
          <select
            value={defaultPriority}
            onChange={(e) => onChangeDefaultPriority(e.target.value as Priority)}
            className="input"
          >
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </select>
        </div>
      </div>

      <label className={`flex cursor-pointer items-start gap-2.5 rounded-lg border p-3 ${autoClassify ? "border-brand bg-brand-50/40" : "border-line bg-white"}`}>
        <input
          type="checkbox"
          checked={autoClassify}
          onChange={(e) => setAutoClassify(e.target.checked)}
          className="mt-0.5"
        />
        <span className="text-xs">
          <span className="font-medium text-ink">Auto-classify into best-fit clusters</span>
          <span className="mt-0.5 block text-muted">
            After import, the Haiku classifier routes each unclustered keyword to the most appropriate existing cluster (≥60% confidence). Keywords that fit no cluster get a “proposed new cluster” hint. ~$0.0001 + ~0.3s per keyword. Assigns to existing clusters only — never auto-creates.
          </span>
        </span>
      </label>

      <div className="rounded-lg bg-canvas-deep/40 p-4 text-xs text-muted">
        <div className="font-semibold text-ink mb-2 flex items-center gap-1.5">
          <Layers className="h-3.5 w-3.5" />
          Summary
        </div>
        <ul className="list-disc pl-5 space-y-1">
          <li>{cannibalization.standalone.length} standalone keywords → 1 article each</li>
          <li>{acceptedGroupsList.length} accepted groupings → {acceptedGroupsList.length} primary article{acceptedGroupsList.length === 1 ? "" : "s"} with {secondariesFolded} secondary keyword{secondariesFolded === 1 ? "" : "s"} folded in</li>
          <li>{cannibalization.groups.length - acceptedGroupsList.length} rejected groupings → those keywords get standalone articles</li>
          <li>Cannibalization risk avoided on {secondariesFolded} potential SERP collision{secondariesFolded === 1 ? "" : "s"}</li>
        </ul>
      </div>

      {commit.isError && (
        <div className="rounded-lg bg-danger-soft px-3 py-2 text-xs text-red-700 flex items-start gap-2">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>{(commit.error as Error).message}</span>
        </div>
      )}

      <Footer
        onBack={onBack}
        onNext={() => commit.mutate()}
        nextLabel={commit.isPending ? (autoClassify ? "Importing + classifying…" : "Importing…") : `Import ${expectedInsert.toLocaleString()} keywords`}
        nextDisabled={commit.isPending}
      />

      {newClusterOpen && (
        <NewClusterDialog
          onCancel={() => setNewClusterOpen(false)}
          onCreated={(clusterId) => {
            setNewClusterOpen(false);
            qc.invalidateQueries({ queryKey: ["pipeline", "clusters"] });
            onChangeDefaultCluster(clusterId);
          }}
        />
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// DONE
// ───────────────────────────────────────────────────────────────────────────

function DoneStep({ result, onClose }: { result: ImportCommitResponse; onClose: () => void }) {
  return (
    <div className="space-y-4 text-center py-6">
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-accent-soft text-emerald-700">
        <CheckCircle2 className="h-6 w-6" strokeWidth={2.2} />
      </div>
      <div>
        <div className="text-lg font-semibold text-ink">Import complete</div>
        <p className="mt-1 text-xs text-muted text-balance">
          {result.inserted} keyword{result.inserted === 1 ? "" : "s"} added to the pipeline
          {result.skipped > 0 && ` · ${result.skipped} skipped (already existed)`}.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-2 max-w-md mx-auto sm:grid-cols-3 text-left">
        <KpiSmall label="Primaries" value={result.primariesCreated} tone="brand" />
        <KpiSmall label="Secondaries" value={result.secondariesCreated} tone="accent" />
        <KpiSmall label="Standalone" value={result.standaloneCreated} />
      </div>

      {result.classified !== undefined && result.classified > 0 && (
        <div className="mx-auto max-w-md rounded-lg bg-brand-50/60 px-3 py-2 text-left text-2xs text-brand-800">
          <CheckCircle2 className="mr-1 inline h-3 w-3" />
          Auto-classified <strong>{result.classified}</strong> keyword{result.classified === 1 ? "" : "s"} into best-fit clusters.
        </div>
      )}
      {result.proposedNewClusters && result.proposedNewClusters.length > 0 && (
        <div className="mx-auto max-w-md rounded-lg bg-warn-soft px-3 py-2 text-left text-2xs text-amber-800">
          <div className="font-semibold mb-1">Suggested new clusters (not created):</div>
          <ul className="list-disc pl-4 space-y-0.5">
            {result.proposedNewClusters.map((p) => (
              <li key={p.id}>{p.nameEn} <span className="font-mono">({p.id})</span> — {p.count} keyword{p.count === 1 ? "" : "s"}</li>
            ))}
          </ul>
          <div className="mt-1 text-amber-700/80">Create these via “New cluster”, then re-run classify.</div>
        </div>
      )}

      {result.warnings.length > 0 && (
        <div className="rounded-lg bg-warn-soft px-3 py-2 text-left text-2xs text-amber-800">
          <div className="font-semibold mb-1">Warnings</div>
          <ul className="list-disc pl-4 space-y-0.5">
            {result.warnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </div>
      )}

      <div className="flex items-center justify-center gap-2 pt-3">
        <button onClick={onClose} className="btn-primary btn-sm">
          <Sparkles className="h-3.5 w-3.5" />
          See them in the pipeline
        </button>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Shared footer
// ───────────────────────────────────────────────────────────────────────────

function Footer({
  onBack,
  onNext,
  nextLabel,
  nextDisabled,
}: {
  onBack: () => void;
  onNext: () => void;
  nextLabel: string;
  nextDisabled?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-2 border-t border-line pt-4">
      <button onClick={onBack} className="btn-secondary btn-sm">
        <ArrowLeft className="h-3.5 w-3.5" />
        Back
      </button>
      <button onClick={onNext} disabled={nextDisabled} className="btn-primary btn-sm">
        {nextLabel}
        <ArrowRight className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
